import { normalizePublicCtaUrl } from "@/lib/auth-urls";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "https://admin.notifystudio.com/api/public";
const SITE_BASE = process.env.NEXT_PUBLIC_SITE_URL || "https://notifystudio.com";

function toHttpFallbackUrl(value: string) {
  if (!value.startsWith("https://")) return null;
  return `http://${value.slice("https://".length)}`;
}

function rewriteLegacyPublicPath(path: string) {
  const [pathAndQuery, hash = ""] = path.split("#", 2);
  const [pathname, query = ""] = pathAndQuery.split("?", 2);
  const normalizedPath = (pathname.toLowerCase().replace(/\/+$/, "") || "/");

  const exactRewrites: Record<string, string> = {
    "/insights": "/resources/insights",
    "/case-studies": "/resources/case-studies",
    "/free-resources": "/resources/free-resources",
    "/resources/use-cases": "/use-cases",
    "/resources/usecases": "/use-cases",
    "/resources/integrations": "/integrations",
    "/contact-us": "/contact",
  };

  let rewritten = exactRewrites[normalizedPath] || normalizedPath;

  if (normalizedPath === "/blog") {
    rewritten = "/resources/insights";
  } else if (normalizedPath.startsWith("/blog/")) {
    rewritten = normalizedPath.replace(/^\/blog\//, "/resources/insights/");
  }

  const querySuffix = query ? `?${query}` : "";
  const hashSuffix = hash ? `#${hash}` : "";
  return `${rewritten}${querySuffix}${hashSuffix}`;
}

export function resolvePublicAssetUrl(path?: string | null) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const origin = API_BASE.replace(/\/api\/public\/?$/, "");
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}

export function resolvePublicPageUrl(path?: string | null) {
  if (!path) return "/";

  const value = path.trim();
  if (!value) return "/";
  const normalizedDirect = normalizePublicCtaUrl(value);
  if (normalizedDirect && normalizedDirect !== value) return normalizedDirect;
  if (value.startsWith("/") || value.startsWith("#")) {
    if (value.startsWith("#")) return value;
    return normalizePublicCtaUrl(rewriteLegacyPublicPath(value));
  }
  if (/^(mailto:|tel:|javascript:)/i.test(value)) return value;

  try {
    const url = new URL(value);
    const siteUrl = new URL(SITE_BASE);
    const apiUrl = new URL(API_BASE);
    const isSiteHost = url.origin === siteUrl.origin;
    const isBackendHost = url.origin === apiUrl.origin.replace(/\/api\/public$/i, "");
    const isFrontendRoutablePath = !url.pathname.startsWith("/api/") && !url.pathname.startsWith("/admin") && !url.pathname.startsWith("/uploads/") && !url.pathname.startsWith("/_next/");

    if (isSiteHost || (isBackendHost && isFrontendRoutablePath)) {
      return normalizePublicCtaUrl(rewriteLegacyPublicPath(`${url.pathname}${url.search}${url.hash}` || "/"));
    }

    return value;
  } catch {
    return value;
  }
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const normalizedApiBase = API_BASE.replace(/\/+$/, "");
  const url = `${normalizedApiBase}${path}`;
  const isServerRuntime = typeof window === "undefined";
  const serverHttpFallbackUrl = isServerRuntime ? toHttpFallbackUrl(url) : null;
  const isFormDataBody = options?.body instanceof FormData;
  const method = (options?.method || "GET").toUpperCase();
  const isFrequentlyUpdatedCmsPath = path === "/navigation"
    || path === "/settings"
    || path === "/brand"
    || path === "/pricing"
    || path.startsWith("/pages/")
    || path.startsWith("/case-studies")
    || path.startsWith("/changelog");

  const makeRequest = (targetUrl: string) => {
    const baseInit: RequestInit = {
      ...options,
      headers: {
        ...(isFormDataBody ? {} : { "Content-Type": "application/json" }),
        ...(options?.headers || {}),
      },
    };

    if (method !== "GET") {
      return fetch(targetUrl, baseInit);
    }

    const nextOptions = (options as RequestInit & { next?: { revalidate?: number | false; tags?: string[] } })?.next || {};

    if (isFrequentlyUpdatedCmsPath) {
      return fetch(targetUrl, {
        ...baseInit,
        next: {
          ...nextOptions,
          revalidate: 60,
        },
      });
    }

    return fetch(targetUrl, {
      ...baseInit,
      next: {
        revalidate: 300,
        ...nextOptions,
      },
    });
  };

  try {
    if (process.env.NODE_ENV !== "production") {
      try {
        // eslint-disable-next-line no-console
        console.log("apiFetch ->", url);
      } catch {}
    }
    const res = await makeRequest(url);

    if (!res.ok) {
      // If in development, attempt a robust local fallback to avoid SSR 404s when env isn't loaded
      if (process.env.NODE_ENV !== "production") {
        try {
          const fallbackBase = "http://localhost:5000/api/public";
          const fallbackRes = await makeRequest(`${fallbackBase}${path}`);
          if (fallbackRes.ok) return fallbackRes.json();
        } catch {}
      }

      const errorBody = await res.json().catch(() => null);
      const errorMessage = errorBody && typeof errorBody === "object" && "error" in errorBody && typeof (errorBody as any).error === "string"
        ? (errorBody as any).error
        : `API error: ${res.status} ${res.statusText}`;
      throw new Error(errorMessage);
    }

    return res.json();
  } catch (err) {
    if (serverHttpFallbackUrl) {
      const networkError = err as { code?: string; message?: string; cause?: { code?: string; message?: string } };
      const errorCode = `${networkError?.code || ""} ${networkError?.cause?.code || ""}`.toUpperCase();
      const errorMessage = `${networkError?.message || ""} ${networkError?.cause?.message || ""}`.toUpperCase();
      const looksLikeSslProtocolIssue = errorCode.includes("ERR_SSL_WRONG_VERSION_NUMBER")
        || errorMessage.includes("WRONG VERSION NUMBER")
        || errorMessage.includes("SSL");

      if (looksLikeSslProtocolIssue) {
        try {
          const fallbackRes = await makeRequest(serverHttpFallbackUrl);
          if (fallbackRes.ok) return fallbackRes.json();
        } catch {}
      }
    }

    // Network-level errors: try local fallback in development, otherwise rethrow
    if (process.env.NODE_ENV !== "production") {
      try {
        const fallbackBase = "http://localhost:5000/api/public";
        const fallbackRes = await makeRequest(`${fallbackBase}${path}`);
        if (fallbackRes.ok) return fallbackRes.json();
      } catch {}
    }

    throw err;
  }
}

// ── Navigation ────────────────────────────────────────────────

export interface NavigationMenuNode {
  id: string;
  title: string;
  url: string;
  description?: string | null;
  iconKey?: string | null;
  target?: string | null;
  cssClass?: string | null;
  columnLabel?: string | null;
  showOnDesktop?: boolean;
  showOnMobile?: boolean;
  children: NavigationMenuNode[];
}

export interface NavigationData {
  headerMenu?: NavigationMenuNode[];
  footerMenu?: NavigationMenuNode[];
  generalServices: { name: string; href: string; icon: string }[];
  services: {
    category: string;
    slug: string;
    icon: string;
    items: { name: string; slug: string }[];
  }[];
  products: { name: string; slug: string; description: string }[];
  industries: { name: string; slug: string; icon: string; description?: string; linkUrl?: string | null; href?: string }[];
  resources: { name: string; href: string; icon: string }[];
  company: { name: string; href: string }[];
  footerLinks: {
    company: { name: string; href: string }[];
    services: { name: string; href: string }[];
    products: { name: string; href: string }[];
    resources: { name: string; href: string }[];
    legal: { name: string; href: string }[];
  };
  social: { name: string; href: string; icon: string }[];
}

export async function getNavigation(): Promise<NavigationData> {
  return apiFetch("/navigation");
}

// ── Slugs (for generateStaticParams) ──────────────────────────

interface SlugsResponse {
  services: { category: string; slug: string }[];
  categories: string[];
  blogs: string[];
  caseStudies: string[];
  changelogs: string[];
  clients: string[];
  industries: string[];
  products: string[];
  integrations: string[];
  useCases: string[];
  jobs: string[];
  pages: string[];
}

export async function getAllSlugs(): Promise<SlugsResponse> {
  return apiFetch("/slugs");
}

// ── Services ──────────────────────────────────────────────────

export async function getServiceCategories() {
  return apiFetch<unknown[]>("/services");
}

export async function getServiceCategory(categorySlug: string) {
  return apiFetch<Record<string, unknown>>(`/services/${categorySlug}`);
}

export async function getServiceDetail(categorySlug: string, serviceSlug: string) {
  return apiFetch<Record<string, unknown>>(`/services/${categorySlug}/${serviceSlug}`);
}

// ── Integrations ─────────────────────────────────────────────

export interface PublicIntegrationCategory {
  id: string;
  name: string;
  slug: string;
  icon?: string | null;
  description?: string | null;
  count: number;
}

export interface PublicIntegrationCard {
  id: string;
  name: string;
  slug: string;
  logo?: string | null;
  shortDescription?: string | null;
  category?: string | null;
  categorySlug?: string | null;
  isFeatured?: boolean;
  setupComplexity?: string | null;
  setupTime?: string | null;
}

export interface PublicIntegrationsResponse {
  integrations: PublicIntegrationCard[];
  categories: PublicIntegrationCategory[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface PublicIntegrationDetail {
  id: string;
  name: string;
  slug: string;
  logo?: string | null;
  websiteUrl?: string | null;
  shortDescription?: string | null;
  fullDescription?: string | null;
  category?: string | null;
  categorySlug?: string | null;
  setupComplexity?: string | null;
  setupTime?: string | null;
  isFeatured?: boolean;
  metaTitle?: string | null;
  metaDescription?: string | null;
  useCases: string[];
  guide?: {
    title: string;
    content?: unknown;
    steps: unknown[];
    screenshots: unknown[];
    videoUrl?: string | null;
  } | null;
}

export interface PublicIntegrationDetailResponse {
  integration: PublicIntegrationDetail;
  relatedIntegrations: Array<{ title: string; href: string; description?: string }>;
}

interface GetPublicIntegrationsOptions {
  search?: string;
  category?: string;
  featured?: boolean;
  page?: number;
  limit?: number;
}

export async function getPublicIntegrations(options: GetPublicIntegrationsOptions = {}) {
  const params = new URLSearchParams();
  if (options.search) params.set("search", options.search);
  if (options.category) params.set("category", options.category);
  if (options.featured) params.set("featured", "true");
  if (options.page) params.set("page", String(options.page));
  if (options.limit) params.set("limit", String(options.limit));

  const query = params.toString();
  return apiFetch<PublicIntegrationsResponse>(`/integrations${query ? `?${query}` : ""}`);
}

export async function getPublicIntegration(slug: string) {
  return apiFetch<PublicIntegrationDetailResponse>(`/integrations/${slug}`);
}

// ── Blog ──────────────────────────────────────────────────────

export interface PublicBlogCategorySummary {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  count: number;
}

export interface PublicBlogTagSummary {
  tag: string;
  count: number;
}

export interface PublicBlogPostPreview {
  id: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  coverImage?: string | null;
  tags?: string[] | string | null;
  author?: string | null;
  publishedAt?: string | null;
  category?: {
    id: string;
    name: string;
    slug: string;
  } | null;
}

export interface PublicBlogListingResponse {
  posts: PublicBlogPostPreview[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  categories: PublicBlogCategorySummary[];
  popularTags: PublicBlogTagSummary[];
}

interface GetBlogPostsOptions {
  page?: number;
  limit?: number;
  category?: string;
  tag?: string;
  search?: string;
  serviceSlug?: string;
  featureSlug?: string;
}

export async function getBlogPosts(options: GetBlogPostsOptions = {}) {
  const params = new URLSearchParams();
  params.set("page", String(options.page ?? 1));
  params.set("limit", String(options.limit ?? 12));

  if (options.category) params.set("category", options.category);
  if (options.tag) params.set("tag", options.tag);
  if (options.search) params.set("search", options.search);
  if (options.serviceSlug) params.set("serviceSlug", options.serviceSlug);
  if (options.featureSlug) params.set("featureSlug", options.featureSlug);

  return apiFetch<PublicBlogListingResponse>(`/blog?${params.toString()}`);
}

export async function getBlogPost(slug: string) {
  return apiFetch<Record<string, unknown>>(`/blog/${slug}`);
}

// ── Case Studies ──────────────────────────────────────────────

export interface PublicCaseStudyPreview {
  id: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  coverImage?: string | null;
  client?: string | null;
  industry?: string | null;
  technologies?: string[];
  featured?: boolean;
  primaryMetric?: string | null;
}

export interface PublicCaseStudyListingResponse {
  studies: PublicCaseStudyPreview[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface GetCaseStudiesOptions {
  page?: number;
  limit?: number;
  featureSlug?: string;
  useCaseSlug?: string;
  blogSlug?: string;
  solutionSlug?: string;
  industry?: string;
  search?: string;
  featured?: boolean;
}

export async function getCaseStudies(page?: number, limit?: number): Promise<PublicCaseStudyListingResponse>;
export async function getCaseStudies(options?: GetCaseStudiesOptions): Promise<PublicCaseStudyListingResponse>;
export async function getCaseStudies(pageOrOptions: number | GetCaseStudiesOptions = 1, maybeLimit = 20): Promise<PublicCaseStudyListingResponse> {
  const options: GetCaseStudiesOptions =
    typeof pageOrOptions === "number"
      ? { page: pageOrOptions, limit: maybeLimit }
      : pageOrOptions;

  const params = new URLSearchParams();
  params.set("page", String(options.page ?? 1));
  params.set("limit", String(options.limit ?? 20));
  if (options.featureSlug) params.set("featureSlug", options.featureSlug);
  if (options.useCaseSlug) params.set("useCaseSlug", options.useCaseSlug);
  if (options.blogSlug) params.set("blogSlug", options.blogSlug);
  if (options.solutionSlug) params.set("solutionSlug", options.solutionSlug);
  if (options.industry) params.set("industry", options.industry);
  if (options.search) params.set("search", options.search);
  if (typeof options.featured === "boolean") params.set("featured", String(options.featured));

  return apiFetch<PublicCaseStudyListingResponse>(`/case-studies?${params.toString()}`);
}

export async function getCaseStudy(slug: string) {
  return apiFetch<Record<string, unknown>>(`/case-studies/${slug}`);
}

// ── Changelog ────────────────────────────────────────────────

export type ChangelogItemType = "feature" | "improvement" | "bugfix" | "security" | "optimization";

export interface PublicChangelogPreview {
  id: string;
  version: string;
  title: string;
  slug: string;
  releaseDate: string;
  summary?: string | null;
  featuredImage?: string | null;
  highlights: Array<{ type: ChangelogItemType; title: string }>;
  counts: Record<ChangelogItemType, number>;
  relatedFeatures: Array<{ title: string; href: string }>;
  relatedIntegrations: Array<{ title: string; href: string }>;
}

export interface PublicChangelogListingResponse {
  releases: PublicChangelogPreview[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface PublicChangelogDetailResponse {
  release: {
    id: string;
    version: string;
    title: string;
    slug: string;
    releaseDate: string;
    summary?: string | null;
    content?: string | null;
    metaTitle?: string | null;
    metaDescription?: string | null;
    featuredImage?: string | null;
  };
  groupedItems: Array<{
    key: ChangelogItemType;
    label: string;
    items: Array<{ id: string; title: string; description?: string | null }>;
  }>;
  relatedFeatures: Array<{ title: string; href: string }>;
  relatedIntegrations: Array<{ title: string; href: string }>;
  relatedBlogs: Array<{ title: string; href: string; description?: string }>;
  relatedUseCases: Array<{ title: string; href: string; description?: string }>;
  relatedReleases: Array<{ title: string; href: string; description?: string }>;
}

interface GetChangelogsOptions {
  page?: number;
  limit?: number;
  search?: string;
  type?: ChangelogItemType;
}

export async function getChangelogs(options: GetChangelogsOptions = {}) {
  const params = new URLSearchParams();
  params.set("page", String(options.page ?? 1));
  params.set("limit", String(options.limit ?? 12));
  if (options.search) params.set("search", options.search);
  if (options.type) params.set("type", options.type);

  return apiFetch<PublicChangelogListingResponse>(`/changelog?${params.toString()}`);
}

export async function getChangelog(slug: string) {
  return apiFetch<PublicChangelogDetailResponse>(`/changelog/${slug}`);
}

// ── Use Cases ──────────────────────────────────────────────────

export interface PublicUseCasePreview {
  id: string;
  title: string;
  slug: string;
  shortDescription?: string | null;
  description?: string | null;
  industry?: string | null;
  isFeatured?: boolean;
  ctaText?: string | null;
  ctaUrl?: string | null;
  featuredImage?: string | null;
  features?: Array<{ id: string; title: string; slug: string }>;
  solutions?: Array<{ id: string; title: string; slug: string }>;
}

export interface PublicUseCasesListing {
  useCases: PublicUseCasePreview[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

interface GetUseCasesOptions {
  page?: number;
  limit?: number;
  featureSlug?: string;
  solutionSlug?: string;
  blogSlug?: string;
  industry?: string;
  search?: string;
  featured?: boolean;
}

export async function getUseCases(options: GetUseCasesOptions = {}) {
  const params = new URLSearchParams();
  if (options.page) params.set("page", String(options.page));
  if (options.limit) params.set("limit", String(options.limit));
  if (options.featureSlug) params.set("featureSlug", options.featureSlug);
  if (options.solutionSlug) params.set("solutionSlug", options.solutionSlug);
  if (options.blogSlug) params.set("blogSlug", options.blogSlug);
  if (options.industry) params.set("industry", options.industry);
  if (options.search) params.set("search", options.search);
  if (typeof options.featured === "boolean") params.set("featured", String(options.featured));

  const query = params.toString();
  return apiFetch<PublicUseCasesListing>(`/use-cases${query ? `?${query}` : ""}`);
}

export async function getUseCase(slug: string) {
  return apiFetch<{
    id: string;
    title: string;
    slug: string;
    shortDescription?: string | null;
    description?: string | null;
    industry?: string | null;
    content?: string | null;
    benefits?: string[] | null;
    ctaText?: string | null;
    ctaUrl?: string | null;
    metaTitle?: string | null;
    metaDescription?: string | null;
    isFeatured?: boolean;
    featuredImage?: string | null;
    features?: Array<{ id: string; title: string; slug: string }>;
    solutions?: Array<{ id: string; title: string; slug: string }>;
    blogs?: Array<{ id: string; title: string; slug: string }>;
    caseStudies?: Array<{ id: string; title: string; slug: string; excerpt?: string | null; industry?: string | null }>;
  }>(`/use-cases/${slug}`);
}


// ── Clients / Portfolio ──────────────────────────────────────

export interface PublicClientCard {
  id: string;
  name: string;
  slug: string;
  websiteUrl?: string | null;
  shortDescription?: string | null;
  isFeatured?: boolean;
  logo?: string | null;
  featuredImage?: string | null;
  industry?: {
    id: string;
    name: string;
    slug: string;
  } | null;
  services: Array<{
    id: string;
    name: string;
    slug: string;
    href: string;
  }>;
  products: Array<{
    id: string;
    name: string;
    slug: string;
    href: string;
  }>;
}

export interface PublicClientListingResponse {
  clients: PublicClientCard[];
  filters: {
    industries: Array<{ value: string; label: string; count: number }>;
    services: Array<{ value: string; label: string; count: number }>;
  };
}

export interface PublicClientDetailResponse {
  client: Record<string, unknown>;
  relatedServices: Array<{
    title: string;
    href: string;
    description?: string;
  }>;
  relatedProducts: Array<{
    title: string;
    href: string;
    description?: string;
  }>;
  relatedCaseStudies: Array<{
    title: string;
    href: string;
    description?: string;
    badge?: string;
  }>;
}

export async function getClients() {
  return apiFetch<PublicClientListingResponse>("/clients");
}

export async function getClient(slug: string) {
  return apiFetch<PublicClientDetailResponse>(`/clients/${slug}`);
}

// ── Industries ────────────────────────────────────────────────

export async function getIndustries() {
  return apiFetch<unknown[]>("/industries");
}

export async function getIndustry(slug: string) {
  return apiFetch<Record<string, unknown>>(`/industries/${slug}`);
}

// ── Products ──────────────────────────────────────────────────

export interface ProductFilterOption {
  value: string;
  label: string;
  count: number;
  categorySlug?: string;
}

export interface ProductCatalogItem {
  id: string;
  name: string;
  slug: string;
  category?: string | null;
  shortDescription?: string | null;
  icon?: string | null;
  image?: string | null;
  featured?: boolean;
  demoUrl?: string | null;
  websiteUrl?: string | null;
  tags: string[];
  services: Array<{
    id: string;
    name: string;
    slug: string;
    categoryName: string;
    categorySlug: string;
  }>;
  industries: Array<{
    id: string;
    name: string;
    slug: string;
  }>;
  screenshot?: {
    id: string;
    filePath: string;
    altText?: string | null;
  } | null;
}

export interface ProductCatalogResponse {
  products: ProductCatalogItem[];
  filters: {
    categories: ProductFilterOption[];
    industries: ProductFilterOption[];
    services: ProductFilterOption[];
    tags: ProductFilterOption[];
  };
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface ProductDetailResponse {
  product: Record<string, unknown>;
  relatedProducts: Array<{
    title: string;
    href: string;
    description?: string;
    badge?: string;
    iconKey?: string;
  }>;
}

interface GetProductsOptions {
  category?: string;
  industry?: string;
  service?: string;
  tag?: string;
  limit?: number;
  page?: number;
}

export async function getProducts(options: GetProductsOptions = {}) {
  const params = new URLSearchParams();
  if (options.category) params.set("category", options.category);
  if (options.industry) params.set("industry", options.industry);
  if (options.service) params.set("service", options.service);
  if (options.tag) params.set("tag", options.tag);
  if (options.limit) params.set("limit", String(options.limit));
  if (options.page) params.set("page", String(options.page));

  const query = params.toString();
  return apiFetch<ProductCatalogResponse>(`/products${query ? `?${query}` : ""}`);
}

export async function getProduct(slug: string) {
  return apiFetch<ProductDetailResponse>(`/products/${slug}`);
}

// ── Brand & Settings ──────────────────────────────────────────

export async function getBrandSettings() {
  return apiFetch<Record<string, string>>("/brand");
}

export async function getPublicSettings() {
  return apiFetch<Record<string, unknown>>("/settings");
}

export interface PublicPricingPlan {
  id: string;
  name: string;
  description?: string | null;
  monthly?: number | null;
  annual?: number | null;
  currency?: string | null; // ISO code
  highlight?: boolean | null;
  image?: string | null;
}

export interface PublicPricingResponse {
  plans: PublicPricingPlan[];
  features: { name: string; values: Array<string | boolean | null> }[];
  currencies?: string[];
}

interface GetPublicPricingOptions {
  currency?: string;
  billing?: "monthly" | "yearly";
}

export async function getPublicPricing(options: GetPublicPricingOptions = {}) {
  const params = new URLSearchParams();
  if (options.currency) params.set("currency", options.currency);
  if (options.billing) params.set("billing", options.billing);
  const query = params.toString();

  return apiFetch<PublicPricingResponse>(`/pricing${query ? `?${query}` : ""}`);
}

function normalizeCtaLabelText(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return value;

  if (/^(book\s+a?\s*demo|request\s+a?\s*demo|view\s+(live\s+)?demo)$/i.test(trimmed)) {
    return "Get Started";
  }

  return value;
}

function sanitizeCmsValue(value: unknown): unknown {
  if (typeof value === "string") {
    return normalizeCtaLabelText(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeCmsValue(item));
  }

  if (value && typeof value === "object") {
    const mappedEntries = Object.entries(value).map(([key, entry]) => [key, sanitizeCmsValue(entry)]);
    return Object.fromEntries(mappedEntries);
  }

  return value;
}

export async function getCmsPage(slug: string) {
  try {
    const page = await apiFetch<Record<string, unknown>>(`/pages/${slug}`);
    return sanitizeCmsValue(page) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export interface PublicTestimonial {
  id: string;
  name: string;
  designation?: string | null;
  company?: string | null;
  feedback: string;
  profileImage?: string | null;
  rating?: number | null;
  status: string;
  featured: boolean;
  sortOrder: number;
}

export interface PublicResourceItem {
  id: string;
  title: string;
  slug: string;
  description?: string | null;
  filePath: string;
  thumbnailPath?: string | null;
  category?: string | null;
  fileTypeLabel?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  status: string;
  sortOrder: number;
}

export async function getPublicTestimonials() {
  return apiFetch<{ testimonials: PublicTestimonial[] }>("/testimonials");
}

export async function getPublicResources() {
  return apiFetch<{ resources: PublicResourceItem[]; categories: string[] }>("/resources");
}

// ── Mutations (client-side only) ──────────────────────────────

export async function submitLead(data: {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  message: string;
  source?: string;
  captchaToken?: string;
}) {
  return apiFetch<{ success: boolean }>("/leads", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function subscribeNewsletter(email: string, captchaToken?: string) {
  return apiFetch<{ success: boolean }>("/newsletter", {
    method: "POST",
    body: JSON.stringify({ email, captchaToken }),
  });
}

// ── Search Index ──────────────────────────────────────────────

export interface SearchIndex {
  services: { name: string; slug: string; categorySlug: string; shortDescription?: string }[];
  blogs: { title: string; slug: string; excerpt?: string; tags?: string }[];
  caseStudies: { title: string; slug: string; excerpt?: string; industry?: string }[];
  industries: { name: string; slug: string; description?: string }[];
  products: { name: string; slug: string; tagline?: string }[];
  resources: { title: string; slug: string; description?: string; category?: string; fileTypeLabel?: string }[];
  features?: { title: string; slug: string; description?: string }[];
  useCases?: { title: string; slug: string; description?: string; industry?: string; features?: string }[];
  integrations?: { name: string; slug: string; description?: string; category?: string }[];
  jobs?: { title: string; slug: string; department?: string; location?: string }[];
}

export async function getSearchIndex(): Promise<SearchIndex> {
  return apiFetch("/search");
}

// ── Careers ───────────────────────────────────────────────────

export interface JobListing {
  id: string;
  title: string;
  slug: string;
  department: string;
  location: string;
  type: string;
  experience: string;
  description: string | null;
  requirements: unknown;
  responsibilities: unknown;
  published: boolean;
  createdAt: string;
}

export async function getJobListings() {
  return apiFetch<{ jobs: JobListing[]; departments: string[] }>("/careers");
}

export async function getJobDetail(slug: string) {
  return apiFetch<{ job: JobListing }>(`/careers/${slug}`);
}

export async function submitApplication(data: FormData | {
  jobId: string;
  name: string;
  email: string;
  phone?: string;
  resumeUrl?: string;
  coverLetter?: string;
  captchaToken?: string;
}) {
  return apiFetch<{ success: boolean }>("/careers/apply", {
    method: "POST",
    body: data instanceof FormData ? data : JSON.stringify(data),
  });
}
